import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import Checkbox from 'apollo-react/components/Checkbox';
import TreeItem from 'apollo-react/components/TreeItem';
import TreeView from 'apollo-react/components/TreeView';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { changePersonalDefaultRepository } from '../../../actions/DocumentRepositoryActions';
import { setLastSelectedSearchName } from '../../../actions/SavedSearchFiltersActions';
import withTranslation from '../../../components/withTranslation';
import { getRepositoryItem } from '../../../utils/commonUtils';

const useStyles = makeStyles({
  treeView: {
    marginTop: 24,
    height: 'auto',
  },
  optionsContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
});

const DocumentTree = ({
  repositoryName = '',
  documentRepositoryDetails,
  onSelectedItem,
  repositoryItems,
  translateString,
  lastSelectedItem,
  defaultRepository,
  isDefaultSetForUser,
}) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState([]);
  const [isOpenAll, setIsOpenAll] = useState(false);
  const [isPersonalDefault, setIsPersonalDefault] = useState(false);
  const [selected, setSelected] = useState('');
  const dispatch = useDispatch();

  const repositoryChildNodes = documentRepositoryDetails.ChildNodes || [];

  const initialRepositoryItem = repositoryChildNodes
    .map((item) => getRepositoryItem(item, lastSelectedItem.nodeId))
    .filter((item) => item !== null)[0];

  const defaultSelectedItem = initialRepositoryItem ?? repositoryChildNodes[0] ?? { NodeId: '' };
  const repositoryItemKey = `${repositoryName} / ${defaultSelectedItem.NodeId}`;

  const selectRepositoryItem = ({ NodeId, NodeText }) => {
    onSelectedItem({ repositoryName, NodeText, queryName: NodeText, nodeId: NodeId });
    setSelected(NodeId);
  };

  useEffect(() => {
    let initialItemPath = [defaultSelectedItem.NodeId];
    if (lastSelectedItem && lastSelectedItem.itemPath?.length) {
      initialItemPath = [...lastSelectedItem.itemPath];
    }
    selectRepositoryItem(defaultSelectedItem);
    setExpanded([...initialItemPath]);
    setIsOpenAll(false);
    setIsPersonalDefault(repositoryName === defaultRepository && isDefaultSetForUser);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repositoryItemKey]);

  const handleSelect = (_, nodeId) => {
    selectRepositoryItem(repositoryItems[nodeId]);

    dispatch(
      setLastSelectedSearchName({
        key: repositoryName,
        searchName: { nodeId, itemPath: expanded },
      })
    );
  };

  const handleExpandedNodes = (_, nodeIds) => setExpanded(nodeIds);

  const toggleExpandAll = (_, checked) => {
    setIsOpenAll(checked);

    if (!checked) {
      setExpanded([]);
    } else {
      setExpanded(
        Object.keys(repositoryItems).filter(
          (nodeId) => repositoryItems[nodeId].ChildNodes.length !== 0
        )
      );
    }
  };

  const togglePersonalDefault = (e, checked) => {
    setIsPersonalDefault(checked);

    if (!checked) {
      dispatch(changePersonalDefaultRepository({ repositoryName, isDelete: true }));
    } else {
      dispatch(changePersonalDefaultRepository({ repositoryName }));
    }
  };

  const renderTreeItems = (node) => {
    return (
      <TreeItem key={node.NodeId} nodeId={node.NodeId} label={translateString(node.NodeText)}>
        {node.ChildNodes.map((child) => renderTreeItems(child))}
      </TreeItem>
    );
  };

  return (
    <>
      <Button variant="secondary" size="small" style={{ marginRight: 16, display: 'none' }}>
        {translateString('Save View')}
      </Button>
      <div className={classes.optionsContainer}>
        <Checkbox
          label={translateString('Set As Default')}
          checked={isPersonalDefault}
          onChange={togglePersonalDefault}
        />
        <Checkbox
          label={translateString('Open All')}
          checked={isOpenAll}
          onChange={toggleExpandAll}
        />
      </div>
      <div className={classes.treeView}>
        <TreeView
          onNodeSelect={handleSelect}
          onNodeToggle={handleExpandedNodes}
          expanded={expanded}
          selected={selected}
        >
          {repositoryChildNodes.map(renderTreeItems)}
        </TreeView>
      </div>
    </>
  );
};

export default withTranslation(DocumentTree);
